import { useEffect, useRef } from 'react';
import type { Appearance, Sex } from '../api/types';
import { PILE_OURO } from '../assets/items';
import { aquecerCabelo, estiloValido, tingirFolhaCabelo } from '../look/cabelo';
import { tingirFolha, tingirFolhaMaterial } from '../look/pele';
import { numero, t } from '../i18n/idioma';
import { MONTARIA_PADRAO } from '../world/montarias';
import {
  carregarFolha, desenharFolha, ehMontaria, estiloDaFolha, materialDaPeca, jsonAnimal,
  jsonBarba, jsonCabelo, jsonCloth, jsonPersonagem, pastaDaCloth, type Folha
} from '../world/spriteAtlas';

export function LojaCarteira({ ouro }: { ouro: number | null }) {
  return (
    <span className="loja-carteira">
      <img src={PILE_OURO} alt="" />
      <b>{ouro == null ? '…' : numero(ouro)}</b>
      <small>{t('loja.noCofre')}</small>
    </span>
  );
}

function reduzido(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** O animal do cartão, parado até o rato entrar ou a oferta estar no provador. */
export function MontariaViva({ id, anda }: { id: string; anda: boolean }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const andaDeFato = anda && !reduzido();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !ehMontaria(id)) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let vivo = true;
    let raf = 0;
    void carregarFolha(jsonAnimal(id, true)).then(folha => {
      if (!vivo) return;
      const tick = (agora: number) => {
        if (!vivo) return;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Os compridos (dragões do pântano, hiryu) têm célula maior que a padrão: a célula tem a âncora no meio,
        // então caber a célula no cartão de 112 é caber a arte. Só encolhe o que não cabe em 0,78.
        const escala = Math.min(0.78, 108 / folha.cellW);
        desenharFolha(ctx, folha, 2, false, 0, andaDeFato ? agora : 0, 56, 98, escala);
        if (andaDeFato) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }).catch(() => {});
    return () => {
      vivo = false;
      cancelAnimationFrame(raf);
    };
  }, [id, andaDeFato]);

  return <canvas ref={ref} className="loja-bicho" width={112} height={112} aria-hidden="true" />;
}

interface Cena {
  animal: Folha | null;
  corpo: Folha | null;
  roupas: Folha[];
  cabelo: Folha | null;
  barba: Folha | null;
}

function pintar(ctx: CanvasRenderingContext2D, w: number, h: number, cena: Cena, dir: number, anda: boolean, agora: number) {
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, w, h);
  const rx = w / 2;
  const ry = h - 26;
  const zoom = 1.15;
  const now = anda ? agora : 0;
  if (cena.animal) desenharFolha(ctx, cena.animal, dir, false, 0, now, rx, ry, zoom);
  if (cena.corpo) desenharFolha(ctx, cena.corpo, dir, false, 0, now, rx, ry, zoom);
  for (const folha of cena.roupas) desenharFolha(ctx, folha, dir, false, 0, now, rx, ry, zoom);
  if (cena.cabelo) desenharFolha(ctx, cena.cabelo, dir, false, 0, now, rx, ry, zoom);
  if (cena.barba) desenharFolha(ctx, cena.barba, dir, false, 0, now, rx, ry, zoom);
}

export function LojaProvador({
  nome, oque, mountId, pele, peleHue, aparencia, sexo, roupa, dir, experimentando, onGirar, onVoltar, soCena = false
}: {
  /** HUD V3: só o canvas (o palco da Loja do Reino desenha o resto). */
  soCena?: boolean;
  nome: string;
  oque: string;
  mountId: string;
  pele: string;
  peleHue: number;
  aparencia: Appearance | null;
  sexo: Sex;
  roupa: string;
  dir: number;
  experimentando: boolean;
  onGirar(passo: number): void;
  onVoltar(): void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const dirRef = useRef(dir);
  dirRef.current = dir;
  const corpoId = pele === 'ghost' || !pele ? (sexo === 'female' ? 'human-female' : 'human-male') : pele;
  const tomPele = aparencia?.skinHue || peleHue;
  const cabeloId = estiloDaFolha(aparencia?.hairStyle) || 'ponytail';
  const cabeloHue = aparencia?.hairHue || 1102;
  const barbaId = sexo === 'female' ? '' : estiloDaFolha(aparencia?.beardStyle);
  const barbaHue = aparencia?.beardHue || cabeloHue;
  const montaria = ehMontaria(mountId) ? mountId : MONTARIA_PADRAO;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let vivo = true;
    let raf = 0;
    const anda = !reduzido();

    const folha = (url: string) => url ? carregarFolha(url).catch(() => null) : Promise.resolve(null);

    void (async () => {
      await aquecerCabelo();
      if (!vivo) return;
      const camadas = (roupa || 'robe').split('+').filter(Boolean);
      const [animal, corpo, ...resto] = await Promise.all([
        folha(jsonAnimal(montaria, true)),
        folha(jsonPersonagem(corpoId, 'horse', 'walking')),
        ...camadas.map(c => folha(jsonCloth(c, 'horse', 'walking'))),
        folha(jsonCabelo(cabeloId, 'horse', 'walking')),
        barbaId ? folha(jsonBarba(barbaId, 'horse', 'walking')) : Promise.resolve(null)
      ]);
      if (!vivo) return;
      const nRoupa = camadas.length;
      const roupas = resto.slice(0, nRoupa).map((f, i) => {
        if (!f) return null;
        return pastaDaCloth(camadas[i]) !== 'robe' ? tingirFolhaMaterial(f, materialDaPeca(camadas[i])) : f;
      }).filter((f): f is Folha => !!f);
      const cabelo = resto[nRoupa];
      const barba = resto[nRoupa + 1];
      const cena: Cena = {
        animal,
        corpo: corpo ? tingirFolha(corpo, tomPele) : null,
        roupas,
        cabelo: cabelo && estiloValido(cabeloId) ? tingirFolhaCabelo(cabelo, cabeloHue) : null,
        barba: barba && estiloValido(barbaId) ? tingirFolhaCabelo(barba, barbaHue) : null
      };
      const tick = (agora: number) => {
        if (!vivo) return;
        pintar(ctx, canvas.width, canvas.height, cena, dirRef.current, anda, agora);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    })().catch(() => {});

    return () => {
      vivo = false;
      cancelAnimationFrame(raf);
    };
  }, [montaria, corpoId, tomPele, roupa, cabeloId, cabeloHue, barbaId, barbaHue]);

  if (soCena) return <canvas ref={ref} className="loja-provador__cena" width={196} height={180} aria-hidden="true" />;
  return (
    <div className="loja-provador">
      <canvas ref={ref} className="loja-provador__cena" width={196} height={180} aria-hidden="true" />
      <div className="loja-provador__txt">
        <h3>{t('loja.provador')}</h3>
        <p>
          {experimentando
            ? t('loja.experimentando', { nome, oque })
            : t('loja.comoEsta', { nome, oque })}
        </p>
        <div className="loja-provador__acoes">
          <span className="loja-provador__girar">
            <button type="button" className="btn btn--ghost btn--tiny" onClick={() => onGirar(-1)} aria-label={t('loja.girarEsq')}>◀</button>
            <button type="button" className="btn btn--ghost btn--tiny" onClick={() => onGirar(1)} aria-label={t('loja.girarDir')}>▶</button>
          </span>
          {experimentando && (
            <button type="button" className="btn btn--ghost btn--tiny" onClick={onVoltar}>{t('loja.voltar')}</button>
          )}
        </div>
      </div>
    </div>
  );
}
